import type { MailThreadListItem } from '../thread-view/thread-types';
import { MAIL_SPLITS, type MailSplitId } from './split-types';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const MAIL_THREAD_LIST_CURSOR_MAX_LENGTH = 2_048;
const MAIL_THREAD_LIST_CURSOR_FIELD_MAX_LENGTH = 512;
const MAIL_THREAD_LIST_CURSOR_VERSION = 1;
const MAIL_SPLIT_IDS = new Set(MAIL_SPLITS.map(({ splitId }) => splitId));

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type MailThreadListCursorKey = {
  v: 1;
  accountId: string;
  splitId: MailSplitId;
  sortRank: number;
  kind: 'thread' | 'draft';
  id: string;
};

export class MailThreadListCursorError extends Error {
  constructor(message = 'Invalid thread-list cursor.') {
    super(message);
    this.name = 'MailThreadListCursorError';
  }
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

export function encodeThreadListCursor(key: MailThreadListCursorKey) {
  const bytes = new TextEncoder().encode(JSON.stringify(key));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

export function decodeThreadListCursor(
  cursor: string,
  scope: { accountId: string; splitId: MailSplitId }
): MailThreadListCursorKey {
  if (
    !cursor ||
    cursor.length > MAIL_THREAD_LIST_CURSOR_MAX_LENGTH ||
    !/^[A-Za-z0-9_-]+$/u.test(cursor)
  ) {
    throw new MailThreadListCursorError();
  }

  let value: unknown;
  try {
    const padded = cursor
      .replaceAll('-', '+')
      .replaceAll('_', '/')
      .padEnd(cursor.length + ((4 - (cursor.length % 4)) % 4), '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    value = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  } catch {
    throw new MailThreadListCursorError();
  }

  const key = value as Partial<MailThreadListCursorKey> | null;
  if (
    typeof key !== 'object' ||
    key === null ||
    key.v !== MAIL_THREAD_LIST_CURSOR_VERSION ||
    typeof key.accountId !== 'string' ||
    key.accountId.length === 0 ||
    key.accountId.length > MAIL_THREAD_LIST_CURSOR_FIELD_MAX_LENGTH ||
    key.accountId !== scope.accountId ||
    typeof key.splitId !== 'string' ||
    !MAIL_SPLIT_IDS.has(key.splitId as MailSplitId) ||
    key.splitId !== scope.splitId ||
    !Number.isSafeInteger(key.sortRank) ||
    (key.kind !== 'thread' && key.kind !== 'draft') ||
    (key.kind === 'draft' && key.splitId !== 'drafts') ||
    typeof key.id !== 'string' ||
    key.id.length === 0 ||
    key.id.length > MAIL_THREAD_LIST_CURSOR_FIELD_MAX_LENGTH
  ) {
    throw new MailThreadListCursorError();
  }

  return key as MailThreadListCursorKey;
}

export function compareThreadListCursorKeys(
  left: Pick<MailThreadListCursorKey, 'id' | 'kind' | 'sortRank'>,
  right: Pick<MailThreadListCursorKey, 'id' | 'kind' | 'sortRank'>
) {
  const rankDelta = left.sortRank - right.sortRank;
  if (rankDelta !== 0) return rankDelta;
  if (left.kind !== right.kind) return left.kind === 'thread' ? -1 : 1;
  return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
}

export function threadListCursorKeyOf(
  item: MailThreadListItem,
  splitId: MailSplitId
): MailThreadListCursorKey {
  return {
    v: MAIL_THREAD_LIST_CURSOR_VERSION,
    accountId: item.accountId,
    splitId,
    sortRank: -item.newestInternalDate,
    kind: item.draftId ? 'draft' : 'thread',
    id: item.threadId,
  };
}
